/** Interface C — Monthly Management Board Pack Portal
 * Manager-only authenticated view. Multi-period P&L, balance trend, supplier
 * deep-dive and print-stylesheet PDF export.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtGBP2, fmtDateUK } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, FilePdf, ChartLineUp } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const TOOLTIP_STYLE = { borderRadius: 6, fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_RADIUS = [3, 3, 0, 0];
const LINE_DOT = { r: 3 };
const INITIAL_DIM = { width: 1, height: 1 };

export default function ManagementBoardPack() {
  const { id } = useParams();
  const [pack, setPack] = useState(null);
  const [periodCount, setPeriodCount] = useState(12);

  const load = useCallback(async () => {
    const { data } = await api.get(`/clients/${id}/board-pack`, {
      params: { period_count: periodCount },
    });
    setPack(data);
  }, [id, periodCount]);

  useEffect(() => { load(); }, [load]);

  if (!pack) {
    return <AppShell title="Loading board pack..." />;
  }

  const { client, period_series, current_period, ytd, wet_suppliers, dry_suppliers, template, data_source, end_date } = pack;

  const pnlRows = template?.layout_json?.length
    ? template.layout_json
    : [
        { id: "rev", heading: "Revenue", classification: "ALL", sign: 1, kind: "revenue" },
        { id: "cos", heading: "Cost of Sales", classification: "ALL", sign: -1, kind: "cos" },
        { id: "gp", heading: "Gross Profit", kind: "gp" },
      ];

  return (
    <AppShell
      subtitle="Manager · Confidential"
      title={`${client.name} · Board Pack`}
      actions={
        <div className="flex items-center gap-3 print:hidden">
          <Select value={String(periodCount)} onValueChange={(v) => setPeriodCount(Number(v))}>
            <SelectTrigger data-testid="period-count-select" className="h-10 rounded-md w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => window.print()} data-testid="board-pdf-btn" className="bg-[#1C1917] hover:bg-[#292524] text-white rounded-md">
            <FilePdf size={14} className="mr-1.5" /> Compile PDF
          </Button>
          <Link to={`/admin/clients/${id}`} className="text-sm text-[#57534E] hover:text-[#1C1917] flex items-center gap-2">
            <ArrowLeft size={16} /> Client
          </Link>
        </div>
      }
    >
      <div className="space-y-10 print:space-y-6" data-testid="board-pack-canvas">
        {/* PAGE 1 — Cover & KPI summary */}
        <section className="print:break-after-page">
          <div className="bg-white border border-[#E7E5E4] rounded-md p-8 card-fade print:border-0 print:p-0">
            <div className="flex items-center gap-2 mb-6">
              <ChartLineUp size={18} weight="duotone" />
              <span className="uppercase text-[10px] tracking-[0.2em] text-[#57534E] font-medium">FLASH · Management Board Pack</span>
            </div>
            <p className="text-xs font-mono text-[#A8A29E] mb-2">Reporting period ending {fmtDateUK(end_date)} · {data_source === "xero" ? "Live data from Xero" : "Demo data"}</p>
            <h2 className="font-display text-3xl tracking-tight mb-8" data-testid="board-client-name">{client.name}</h2>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <KpiBlock label="Revenue · Current Period" value={fmtGBP2((current_period.wet_sales || 0) + (current_period.dry_sales || 0))} />
              <KpiBlock label="Cost of Sales · Current" value={fmtGBP2((current_period.wet_purchases || 0) + (current_period.dry_purchases || 0))} />
              <KpiBlock label="Blended GP%" value={`${(current_period.blended_gp || 0).toFixed(1)}%`} />
              <KpiBlock label="YTD Revenue" value={fmtGBP2((ytd.wet_sales || 0) + (ytd.dry_sales || 0))} />
            </div>
          </div>
        </section>

        {/* PAGE 2 — Multi-period P&L */}
        <section className="print:break-after-page print:break-inside-avoid">
          <div className="bg-white border border-[#E7E5E4] rounded-md card-fade overflow-x-auto">
            <div className="p-6 border-b border-[#E7E5E4]">
              <p className="uppercase text-[10px] tracking-[0.2em] text-[#57534E] font-medium">Section 1</p>
              <h3 className="font-display text-2xl tracking-tight">Profit &amp; Loss · {period_series.length}-period view</h3>
            </div>
            <Table data-testid="pnl-table">
              <TableHeader>
                <TableRow className="border-[#E7E5E4]">
                  <TableHead className="text-[10px] uppercase tracking-wider text-[#57534E] px-6 h-10 sticky left-0 bg-white">Line</TableHead>
                  {period_series.map((p) => (
                    <TableHead key={p.month} className="text-[10px] uppercase tracking-wider text-[#57534E] text-right h-10 font-mono-num">{p.month}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <PnlRow label="Beverage Sales" period_series={period_series} field="wet_sales" />
                <PnlRow label="Food Sales" period_series={period_series} field="dry_sales" />
                <PnlRow label="Total Revenue" period_series={period_series} compute={(p) => (p.wet_sales || 0) + (p.dry_sales || 0)} bold />
                <PnlRow label="Beverage CoS" period_series={period_series} field="wet_purchases" negative />
                <PnlRow label="Food CoS" period_series={period_series} field="dry_purchases" negative />
                <PnlRow label="Total CoS" period_series={period_series} compute={(p) => -((p.wet_purchases || 0) + (p.dry_purchases || 0))} bold />
                <PnlRow label="Gross Profit" period_series={period_series} compute={(p) => (p.wet_sales || 0) + (p.dry_sales || 0) - (p.wet_purchases || 0) - (p.dry_purchases || 0)} bold accent="#059669" />
                <PnlRow label="Blended GP%" period_series={period_series} field="blended_gp" suffix="%" accent="#334155" />
                {pnlRows.filter((r) => r.kind && !["revenue", "cos", "gp"].includes(r.kind)).map((r) => (
                  <TableRow key={r.id}><TableCell className="px-6 text-xs">{r.heading}</TableCell>{period_series.map((p) => <TableCell key={p.month} className="text-right font-mono-num text-xs">—</TableCell>)}</TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        {/* PAGE 3 — Trend charts */}
        <section className="print:break-after-page print:break-inside-avoid grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Revenue Trend">
            <BarChart data={period_series}>
              <CartesianGrid stroke="#E7E5E4" vertical={false} />
              <XAxis dataKey="month" stroke="#A8A29E" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#A8A29E" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="wet_sales" name="Beverage" fill="#059669" radius={BAR_RADIUS} barSize={14} />
              <Bar dataKey="dry_sales" name="Food" fill="#334155" radius={BAR_RADIUS} barSize={14} />
            </BarChart>
          </ChartCard>
          <ChartCard title="Gross Profit % · Trend">
            <LineChart data={period_series}>
              <CartesianGrid stroke="#E7E5E4" vertical={false} />
              <XAxis dataKey="month" stroke="#A8A29E" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#A8A29E" fontSize={11} tickLine={false} axisLine={false} unit="%" />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line dataKey="blended_gp" name="Blended GP%" stroke="#1C1917" strokeWidth={2} dot={LINE_DOT} />
              <Line dataKey="wet_gp" name="Beverage GP%" stroke="#059669" strokeWidth={2} dot={false} />
              <Line dataKey="dry_gp" name="Food GP%" stroke="#334155" strokeWidth={2} dot={false} />
            </LineChart>
          </ChartCard>
        </section>

        {/* PAGE 4 — Top suppliers */}
        <section className="print:break-inside-avoid grid grid-cols-1 lg:grid-cols-2 gap-5">
          <SupplierCard title="Beverage Suppliers · Current Period" rows={wet_suppliers} accent="#991B1B" testid="wet-suppliers-table" />
          <SupplierCard title="Food Suppliers · Current Period" rows={dry_suppliers} accent="#991B1B" testid="dry-suppliers-table" />
        </section>

        <p className="text-center text-[10px] text-[#A8A29E] font-mono pt-8 print:pt-2">
          Compiled by FLASH · Prysm Financial · {fmtDateUK(end_date)} · This document contains management figures derived from reconciled accounting feeds.
        </p>
      </div>
    </AppShell>
  );
}

function KpiBlock({ label, value }) {
  return (
    <div className="border-l-2 border-[#1C1917] pl-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#57534E] font-mono mb-1">{label}</p>
      <p className="font-mono-num text-2xl tracking-tight" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value}</p>
    </div>
  );
}

function PnlRow({ label, period_series, field, compute, bold, suffix, accent, negative }) {
  return (
    <TableRow className={`border-[#F5F5F4] ${bold ? "bg-[#FAFAF9]" : ""}`}>
      <TableCell className={`px-6 text-xs sticky left-0 ${bold ? "font-medium bg-[#FAFAF9]" : "bg-white"}`} style={accent ? { color: accent } : undefined}>{label}</TableCell>
      {period_series.map((p) => {
        const raw = compute ? compute(p) : p[field];
        const val = suffix === "%" ? `${(raw ?? 0).toFixed(1)}%` : fmtGBP2(negative ? -(raw ?? 0) : (raw ?? 0));
        return (
          <TableCell key={p.month} className={`text-right font-mono-num text-xs ${bold ? "font-medium" : ""}`} style={accent ? { color: accent } : undefined}>
            {val}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white border border-[#E7E5E4] rounded-md p-6 card-fade">
      <p className="uppercase text-[10px] tracking-[0.2em] text-[#57534E] font-medium mb-4">{title}</p>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_DIM}>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

function SupplierCard({ title, rows, accent, testid }) {
  return (
    <div className="bg-white border border-[#E7E5E4] rounded-md card-fade" data-testid={testid}>
      <div className="p-5 border-b border-[#E7E5E4]">
        <p className="uppercase text-[10px] tracking-[0.2em] font-medium" style={{ color: accent }}>{title}</p>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-[#E7E5E4]">
            <TableHead className="text-[10px] uppercase tracking-wider text-[#57534E] px-5 h-10">Supplier</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-[#57534E] text-right h-10">Invoices</TableHead>
            <TableHead className="text-[10px] uppercase tracking-wider text-[#57534E] text-right h-10 pr-5">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={3} className="text-center text-xs text-[#A8A29E] py-8">No supplier activity in period</TableCell></TableRow>
          ) : rows.map((r) => (
            <TableRow key={r.supplier} className="border-[#F5F5F4]">
              <TableCell className="text-xs px-5 py-2.5">{r.supplier}</TableCell>
              <TableCell className="text-xs font-mono-num text-right py-2.5">{r.count}</TableCell>
              <TableCell className="text-xs font-mono-num text-right py-2.5 pr-5" style={{ color: accent }}>{fmtGBP2(r.total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
